import {
  type CompositionV2DataQuery,
  type CompositionV3DataQuery,
  EntKind,
} from '@revelio/data-access';

import {
  type CompositionV3FullMetrics,
  isCompositionV3FullMetrics,
} from './composition-v3-graphql-response';
import { isEmptyCompositionBucket } from './is-empty-composition-bucket';

type LegacyComposition = NonNullable<CompositionV2DataQuery['composition_v2']>;
type V21Payload = NonNullable<CompositionV3DataQuery['composition_v3']>;

type BreakdownRow = NonNullable<CompositionV3FullMetrics['geography']>[number];

/**
 * Maps a V3 `EntKind` to the legacy V2 `EntMetadata.type` string
 */
function entKindToEntityType(entKind: EntKind | undefined): string {
  if (entKind === undefined) {
    return '';
  }
  switch (entKind) {
    case EntKind.Company:
      return 'DATA_KIND_COMPANY';
    case EntKind.Region:
      return 'DATA_KIND_REGION';
    case EntKind.Country:
      return 'DATA_KIND_COUNTRY';
    case EntKind.State:
      return 'DATA_KIND_STATE';
    case EntKind.Msa:
      return 'DATA_KIND_MSA';
    case EntKind.City:
      return 'DATA_KIND_CITY';
    case EntKind.RicsK10:
      return 'DATA_KIND_RICS_K10';
    case EntKind.RicsK50:
      return 'DATA_KIND_RICS_K50';
    case EntKind.RicsK400:
      return 'DATA_KIND_RICS_K400';
    default:
      return '';
  }
}

const mapBreakdown = (rows: BreakdownRow[] | undefined) =>
  (rows ?? [])
    .filter((row) => !isEmptyCompositionBucket(row.id))
    .map((row) => ({
      metadata: {
        id: row.id,
        short_name: row.short_name,
        long_name: row.long_name,
      },
      timeseries: row.headcount.map((p) => ({
        id: p.id,
        count: p.count,
        share: p.share,
      })),
    }));

export function adaptCompositionV3ToLegacyCompositionV2ForTreemap(
  payload: V21Payload,
  monthRefs: string[],
  entKind: EntKind | undefined
): LegacyComposition {
  const entityType = entKindToEntityType(entKind);
  return {
    refs: { months: monthRefs },
    entities: (payload.entities ?? []).map((e) => {
      const m = isCompositionV3FullMetrics(e.metrics) ? e.metrics : undefined;
      return {
        metadata: {
          id: e.id,
          short_name: e.short_name,
          long_name: e.long_name,
          type: entityType,
        },
        metrics: {
          headcount: (e.metrics?.headcount ?? []).map((p) => ({
            id: p.id,
            value: p.val,
          })),
          salary: (m?.salary ?? []).map((p) => ({ id: p.id, value: p.val })),
          tenure: (m?.tenure ?? []).map((p) => ({ id: p.id, value: p.val })),
          growth_rate: (m?.growth_rate ?? []).map((p) => ({
            id: p.id,
            value: p.val,
          })),
          hiring_rate: (m?.hiring_rate ?? []).map((p) => ({
            id: p.id,
            value: p.val,
          })),
          attrition_rate: (m?.attrition_rate ?? []).map((p) => ({
            id: p.id,
            value: p.val,
          })),
          geography: mapBreakdown(m?.geography),
          role: mapBreakdown(m?.role),
          skills: mapBreakdown(m?.skills),
          rics: mapBreakdown(m?.rics),
          gender: mapBreakdown(m?.gender),
          ethnicity: mapBreakdown(m?.ethnicity),
          education: mapBreakdown(m?.education),
          seniority: mapBreakdown(m?.seniority),
        },
      };
    }),
  } satisfies LegacyComposition;
}
